import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";

import { 
  SearchIcon,
  ChevronRightIcon,
  LocationMarkerIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  PhoneIcon,
  MailIcon,
  CheckIcon,
  XIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  StarIcon,
  ShieldCheckIcon,
  UsersIcon
} from "@heroicons/react/outline";
import Footer from "./Footer";

const BuyerOnboarding = () => {
  const [currentStep, setCurrentStep] = useState(0); // 0 = hero search, 1-7 = form steps

  const [formData, setFormData] = useState({
    need: "",
    category: "",
    quantity: "",
    frequency: "", // one-time or recurring
    urgency: "",
    location: "",
    budget: "",
    fullName: "",
    whatsapp: "",
    email: "",
    allowWhatsapp: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Fetch categories from backend API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/getCategories');
        const data = await response.json();
        
        if (data.success && data.categories) {
          // Sort categories to prioritize specific ones
          const sortedCategories = data.categories.sort((a, b) => {
            // Priority categories that should come first
            const priorityCategories = ['Printing & Packaging', 'Housing & Rentals'];
            
            const aIndex = priorityCategories.indexOf(a.name);
            const bIndex = priorityCategories.indexOf(b.name);
            
            // If both are priority categories, maintain their order
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }
            
            // If only a is priority, a comes first
            if (aIndex !== -1) {
              return -1;
            }
            
            // If only b is priority, b comes first
            if (bIndex !== -1) {
              return 1;
            }
            
            // If neither is priority, maintain alphabetical order
            return a.name.localeCompare(b.name);
          });
          
          // Transform backend categories to match the expected format
          const transformedCategories = sortedCategories.map(category => ({
            icon: category.icon || "📦",
            name: category.name,
            description: category.description || category.name,
            examples: category.examples || [category.name.toLowerCase()]
          }));
          setCategories(transformedCategories);
        } else {
          console.error('Failed to fetch categories:', data.error);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const urgencyOptions = [
    { value: "urgent", label: "Urgent (Within 24 hours)", icon: "🚨" },
    { value: "this_week", label: "This Week", icon: "📅" },
    { value: "custom", label: "Choose date", icon: "📆" }
  ];

  const budgetOptions = [
    { value: "low", label: "Low", range: "₦10,000 - ₦50,000" },
    { value: "medium", label: "Medium", range: "₦50,000 - ₦200,000" },
    { value: "premium", label: "Premium", range: "₦200,000+" }
  ];



  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategorySelect = (category) => {
    // Navigate to create-rfq page with the selected category and start at step 2
    navigate('/create-rfq', { 
      state: { 
        selectedCategory: category.name,
        categoryId: category.id
      } 
    });
  };

  const nextStep = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else if (currentStep === 1) {
      setCurrentStep(0); // Go back to hero search
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setShowSuccess(true);
  };

  // Hero Section (Thumbtack-style)
  const renderHeroSection = () => (
    <div className="bg-gradient-to-br from-orange-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&h=1080&fit=crop&crop=center" 
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="relative z-10">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/">
                <svg
                width="134"
                height="41"
                viewBox="0 0 134 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 lg:h-10"
              >
                <rect
                  width="21.5018"
                  height="21.5019"
                  transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.2529 7.83353)"
                  fill="#CC5500"
                />
                <rect
                  width="21.5018"
                  height="21.5019"
                  transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.3063 3)"
                  fill="#CC5500"
                />
                <rect
                  width="14.7925"
                  height="14.7925"
                  transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.6796 7.67464)"
                  fill="white"
                />
                <rect
                  width="10.75"
                  height="10.5265"
                  transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.7197 8.11559)"
                  fill="#CC5500"
                />
                <path
                  d="M45.8024 31.62V12.69H38.2124V10.62H55.7024V12.69H48.1124V31.62H45.8024ZM58.9051 31.62L54.6751 16.59H56.8651L60.1051 28.59L63.8551 16.59H65.8351L69.5551 28.59L72.8251 16.59H74.9251L70.6651 31.62H68.5351L64.8151 19.59L61.0651 31.62H58.9051ZM83.9959 31.89C82.5559 31.89 81.2459 31.55 80.0659 30.87C78.9059 30.17 77.9759 29.23 77.2759 28.05C76.5959 26.87 76.2559 25.55 76.2559 24.09C76.2559 22.65 76.5859 21.35 77.2459 20.19C77.9059 19.01 78.7959 18.08 79.9159 17.4C81.0359 16.7 82.2859 16.35 83.6659 16.35C85.0059 16.35 86.2059 16.7 87.2659 17.4C88.3459 18.08 89.1959 19.01 89.8159 20.19C90.4559 21.35 90.7759 22.65 90.7759 24.09V24.78H78.4459C78.5659 25.76 78.8759 26.64 79.3759 27.42C79.8959 28.2 80.5659 28.82 81.3859 29.28C82.2059 29.72 83.1059 29.94 84.0859 29.94C84.8659 29.94 85.6259 29.82 86.3659 29.58C87.1059 29.34 87.7259 29 88.2259 28.56L89.6059 30.03C88.7659 30.65 87.8859 31.12 86.9659 31.44C86.0659 31.74 85.0759 31.89 83.9959 31.89ZM78.5059 23.01H88.5559C88.4359 22.11 88.1359 21.31 87.6559 20.61C87.1959 19.89 86.6159 19.33 85.9159 18.93C85.2159 18.51 84.4459 18.3 83.6059 18.3C82.7459 18.3 81.9559 18.5 81.2359 18.9C80.5159 19.3 79.9159 19.86 79.4359 20.58C78.9559 21.28 78.6459 22.09 78.5059 23.01ZM93.8795 31.62V16.59H96.0995V18.72C96.5595 17.94 97.1395 17.34 97.8395 16.92C98.5595 16.48 99.3795 16.26 100.299 16.26C100.819 16.26 101.289 16.33 101.709 16.47V18.51C101.449 18.41 101.189 18.34 100.929 18.3C100.669 18.26 100.409 18.24 100.149 18.24C99.2295 18.24 98.4195 18.51 97.7195 19.05C97.0395 19.57 96.4995 20.33 96.0995 21.33V31.62H93.8795ZM110.156 31.89C108.736 31.89 107.446 31.55 106.286 30.87C105.126 30.17 104.206 29.23 103.526 28.05C102.846 26.87 102.506 25.55 102.506 24.09C102.506 22.63 102.846 21.32 103.526 20.16C104.206 18.98 105.126 18.04 106.286 17.34C107.446 16.64 108.736 16.29 110.156 16.29C111.256 16.29 112.316 16.5 113.336 16.92C114.356 17.32 115.256 17.9 116.036 18.66L114.626 20.28C114.026 19.64 113.336 19.16 112.556 18.84C111.796 18.5 111.006 18.33 110.186 18.33C109.166 18.33 108.236 18.59 107.396 19.11C106.576 19.61 105.916 20.3 105.416 21.18C104.936 22.04 104.696 23.01 104.696 24.09C104.696 25.17 104.936 26.15 105.416 27.03C105.916 27.91 106.586 28.61 107.426 29.13C108.266 29.63 109.196 29.88 110.216 29.88C111.036 29.88 111.806 29.72 112.526 29.4C113.266 29.08 113.956 28.61 114.596 27.99L115.976 29.49C115.176 30.25 114.276 30.84 113.276 31.26C112.276 31.68 111.236 31.89 110.156 31.89ZM125.187 31.89C123.747 31.89 122.437 31.55 121.257 30.87C120.097 30.17 119.167 29.23 118.467 28.05C117.787 26.87 117.447 25.55 117.447 24.09C117.447 22.65 117.777 21.35 118.437 20.19C119.097 19.01 119.987 18.08 121.107 17.4C122.227 16.7 123.477 16.35 124.857 16.35C126.197 16.35 127.397 16.7 128.457 17.4C129.537 18.08 130.387 19.01 131.007 20.19C131.647 21.35 131.967 22.65 131.967 24.09V24.78H119.637C119.757 25.76 120.067 26.64 120.567 27.42C121.087 28.2 121.757 28.82 122.577 29.28C123.397 29.72 124.297 29.94 125.277 29.94C126.057 29.94 126.817 29.82 127.557 29.58C128.297 29.34 128.917 29 129.417 28.56L130.797 30.03C129.957 30.65 129.077 31.12 128.157 31.44C127.257 31.74 126.267 31.89 125.187 31.89ZM119.697 23.01H129.747C129.627 22.11 129.327 21.31 128.847 20.61C128.387 19.89 127.807 19.33 127.107 18.93C126.407 18.51 125.637 18.3 124.797 18.3C123.937 18.3 123.147 18.5 122.427 18.9C121.707 19.3 121.107 19.86 120.627 20.58C120.147 21.28 119.837 22.09 119.697 23.01Z"
                  fill="#CC5500"
                />
              </svg>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Content */}
      <main className="flex items-center justify-center min-h-screen px-4 pt-20 lg:pt-24" role="main">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-left">
              {/* Main Heading */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display text-gray-900 mb-4 md:mb-6">
                Tell Us What You Need.
                <span className="text-orange-600"> We'll Bring the Right Vendors to You.</span>
              </h1>
              
              {/* Subheading */}
              <p className="text-lg md:text-xl text-gray-600 mb-4 max-w-2xl font-body">
                Post what you need — from printing and food supply to cleaning and repairs — and get offers from multiple verified vendors in real time.
              </p>
              <p className="text-base md:text-lg text-orange-600 font-semibold mb-6 md:mb-8 flex items-center">
                <span className="mr-2">⚡</span>
                <span className="hidden sm:inline">Get offers within 2 hours • </span>No registration required
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8 relative">
                {/* Floating notification badge */}
                <div className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse z-50">
                  🔥 127 requests today
                </div>
                <button 
                  onClick={() => navigate('/create-rfq')}
                  className="group relative bg-gradient-to-r from-orange-500 to-red-500 text-white px-10 py-5 rounded-2xl font-bold text-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 font-heading flex items-center justify-center"
                >
                  <div className="flex items-center">
                    <span className="mr-3 text-2xl">🚀</span>
                    <div className="text-left">
                      <div className="text-sm opacity-90">Start Now - 100% Free</div>
                      <div>Post Your Request</div>
                    </div>
                    <ChevronRightIcon className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </div>
                  {/* Animated background effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
                <button 
                  onClick={() => navigate('/signup')}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-5 rounded-2xl font-semibold text-lg hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-all duration-300 font-heading flex items-center"
                >
                  <UsersIcon className="h-5 w-5 mr-2" />
                  Join as Vendor
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-3 text-gray-600 mb-4">
                <div className="flex items-center bg-green-50 px-3 py-2 rounded-lg">
                  <ShieldCheckIcon className="w-4 h-4 mr-2 text-green-600" />
                  <span className="text-sm font-medium text-green-800">2000+ Vendors</span>
                </div>
                <div className="flex items-center bg-yellow-50 px-3 py-2 rounded-lg">
                  <StarIcon className="w-4 h-4 mr-2 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-800">4.8/5 Rating</span>
                </div>
              </div>

              {/* Social Proof */}
              <div className="hidden sm:block mt-6 p-4 bg-gray-50 rounded-xl border-l-4 border-orange-500">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 font-bold">A</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700 italic">
                      "Got 4 quotes for branded paper bags in just 3 hours. Saved 25% compared to my usual supplier!"
                    </p>
                    <p className="text-xs text-gray-500 mt-1">— Orente Grills - Lagos</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Service Categories Grid */}
            <div className="relative hidden lg:block">
              <div className="relative">
                <div className="bg-gradient-to-br from-orange-100 to-blue-100 rounded-3xl p-8 lg:p-12">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl p-6 shadow-lg transform rotate-3 hover:rotate-0 transition-transform">
                      <div className="w-16 h-16 mb-4 mx-auto">
                        <img src="https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=64&h=64&fit=crop&crop=center" alt="Printing and packaging services" className="w-full h-full object-cover rounded-xl" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Printing & Packaging</h4>
                      <p className="text-sm text-gray-600">Business cards, flyers, paper bags</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg transform -rotate-3 hover:rotate-0 transition-transform">
                      <div className="w-16 h-16 mb-4 mx-auto">
                        <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=64&h=64&fit=crop&crop=center" alt="Professional cleaning services" className="w-full h-full object-cover rounded-xl" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Cleaning Services</h4>
                      <p className="text-sm text-gray-600">Office, carpet, window cleaning</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg transform rotate-2 hover:rotate-0 transition-transform">
                      <div className="w-16 h-16 mb-4 mx-auto">
                        <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=64&h=64&fit=crop&crop=center" alt="Fresh food supply" className="w-full h-full object-cover rounded-xl" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Food Supply</h4>
                      <p className="text-sm text-gray-600">Rice, vegetables, catering</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-lg transform -rotate-2 hover:rotate-0 transition-transform">
                      <div className="w-16 h-16 mb-4 mx-auto">
                        <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=64&h=64&fit=crop&crop=center" alt="Professional trades and repairs" className="w-full h-full object-cover rounded-xl" />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Trades & Repairs</h4>
                      <p className="text-sm text-gray-600">Plumbing, electrical, carpentry</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>



          {/* Popular Categories */}
          <div className="mt-16 relative z-20">
            <section aria-labelledby="categories-heading">
              <h2 id="categories-heading" className="text-center text-2xl font-heading text-gray-900 mb-8">Popular Needs</h2>
              <div className="flex flex-wrap justify-center gap-4">
                {loadingCategories ? (
                  // Loading skeleton
                  Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl border border-gray-100 animate-pulse w-36 h-36"
                    >
                      <div className="w-12 h-12 bg-gray-200 rounded-full mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </div>
                  ))
                ) : (
                  categories.map((category, index) => {
                    // Map category names to relevant Unsplash images
                    const getCategoryImage = (categoryName) => {
                      const imageMap = {
                        'Printing & Packaging': 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=80&h=80&fit=crop&crop=center',
                        'Housing & Rentals': 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=80&h=80&fit=crop&crop=center',
                        'Cleaning Services': 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=80&h=80&fit=crop&crop=center',
                        'Food Supply': 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=80&h=80&fit=crop&crop=center',
                        'Trade & Repairs': 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=80&h=80&fit=crop&crop=center',
                        'IT & Technology': 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=80&h=80&fit=crop&crop=center',
                        'Marketing & Advertising': 'https://images.unsplash.com/photo-1557838923-2985c318be48?w=80&h=80&fit=crop&crop=center',
                        'Transportation & Logistics': 'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=80&h=80&fit=crop&crop=center',
                        'Healthcare & Medical': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=80&h=80&fit=crop&crop=center',
                        'Education & Training': 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=80&h=80&fit=crop&crop=center',
                        'Legal Services': 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=80&h=80&fit=crop&crop=center',
                        'Financial Services': 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=80&h=80&fit=crop&crop=center'
                      };
                      return imageMap[categoryName] || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=80&h=80&fit=crop&crop=center';
                    };

                    return (
                      <button
                        key={index}
                        onClick={() => handleCategorySelect(category)}
                        className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl hover:shadow-xl transition-all border border-gray-100 hover:border-orange-200 hover:scale-105 text-center w-36 h-36 relative z-10"
                        aria-label={`Browse ${category.name} services`}
                      >
                        <div className="w-20 h-20 mb-3 rounded-xl overflow-hidden shadow-sm flex items-center justify-center">
                          <img 
                            src={getCategoryImage(category.name)}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 text-center leading-tight">{category.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Mobile Interactive Demo Section */}
      <section className="py-16 lg:py-24 bg-white lg:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
              See It In Action
            </div>
            <h2 className="text-3xl lg:text-4xl font-display text-gray-900 mb-6">
              Experience Twerce on Your Phone
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed font-body">
              Watch how easy it is to post requests and get competitive quotes from verified vendors in minutes.
            </p>
          </div>

          {/* Mobile Demo Container */}
          <div className="lg:hidden relative z-0">
            <div className="relative max-w-sm mx-auto">
              {/* Phone Frame */}
              <div className="relative mx-auto w-80 h-[600px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                  
                  {/* Mobile App Screens */}
                  <div className="relative h-full">
                    
                    {/* Screen 1: Category Selection */}
                    <div className="absolute inset-0 bg-gradient-to-b from-orange-50 to-blue-50 p-6">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                            <span className="text-white text-lg font-bold">T</span>
                          </div>
                          <div className="text-lg font-bold text-gray-800">Twerce</div>
                        </div>
                        <div className="text-sm text-gray-500">1/4</div>
                      </div>
                      
                      {/* Content */}
                      <div className="space-y-4">
                        <div className="text-lg font-semibold text-gray-800 mb-4">What do you need?</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-orange-100 border-2 border-orange-300 rounded-2xl p-4 text-center">
                            <div className="text-2xl mb-2">📦</div>
                            <div className="text-sm font-semibold text-orange-700">Printing & Packaging</div>
                          </div>
                          <div className="bg-gray-100 border-2 border-gray-200 rounded-2xl p-4 text-center">
                            <div className="text-2xl mb-2">🏠</div>
                            <div className="text-sm font-semibold text-gray-600">Housing & Rentals</div>
                          </div>
                          <div className="bg-gray-100 border-2 border-gray-200 rounded-2xl p-4 text-center">
                            <div className="text-2xl mb-2">🧹</div>
                            <div className="text-sm font-semibold text-gray-600">Cleaning Services</div>
                          </div>
                          <div className="bg-gray-100 border-2 border-gray-200 rounded-2xl p-4 text-center">
                            <div className="text-2xl mb-2">🔧</div>
                            <div className="text-sm font-semibold text-gray-600">Trade & Repairs</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Screen 2: Request Details */}
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-green-50 p-6 opacity-0 pointer-events-none transition-opacity duration-500">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                            <span className="text-white text-lg font-bold">T</span>
                          </div>
                          <div className="text-lg font-bold text-gray-800">Twerce</div>
                        </div>
                        <div className="text-sm text-gray-500">2/4</div>
                      </div>
                      
                      {/* Content */}
                      <div className="space-y-4">
                        <div className="text-lg font-semibold text-gray-800 mb-4">Describe your requirement</div>
                        <div className="bg-white rounded-2xl p-4 border border-gray-200">
                          <div className="text-sm text-gray-600 mb-3">📝</div>
                          <div className="text-sm text-gray-500 italic">"I need 500 branded paper bags for my restaurant, A4 size, 300gsm paper, full color printing with my logo..."</div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <div className="bg-orange-100 text-orange-700 text-sm px-3 py-2 rounded-full">Quantity: 500</div>
                          <div className="bg-blue-100 text-blue-700 text-sm px-3 py-2 rounded-full">Budget: ₦50k-100k</div>
                          <div className="bg-green-100 text-green-700 text-sm px-3 py-2 rounded-full">Timeline: 1 week</div>
                        </div>
                      </div>
                    </div>

                    {/* Screen 3: Quotes Received */}
                    <div className="absolute inset-0 bg-gradient-to-b from-green-50 to-purple-50 p-6 opacity-0 pointer-events-none transition-opacity duration-500">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                            <span className="text-white text-lg font-bold">T</span>
                          </div>
                          <div className="text-lg font-bold text-gray-800">Twerce</div>
                        </div>
                        <div className="text-sm text-gray-500">3/4</div>
                      </div>
                      
                      {/* Content */}
                      <div className="space-y-4">
                        <div className="text-lg font-semibold text-gray-800 mb-4">Offers Received (4)</div>
                        <div className="space-y-3">
                          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                            <div className="flex justify-between items-center">
                              <div className="text-sm font-semibold text-green-700">PrintPro Nigeria</div>
                              <div className="text-lg font-bold text-green-700">₦75,000</div>
                            </div>
                            <div className="text-xs text-gray-500 mt-2">⭐ 4.8 • 2 hours ago</div>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                            <div className="flex justify-between items-center">
                              <div className="text-sm font-semibold text-blue-700">Lagos Print Solutions</div>
                              <div className="text-lg font-bold text-blue-700">₦82,000</div>
                            </div>
                            <div className="text-xs text-gray-500 mt-2">⭐ 4.6 • 1 hour ago</div>
                          </div>
                          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
                            <div className="flex justify-between items-center">
                              <div className="text-sm font-semibold text-purple-700">Quick Print Express</div>
                              <div className="text-lg font-bold text-purple-700">₦68,000</div>
                            </div>
                            <div className="text-xs text-gray-500 mt-2">⭐ 4.9 • 30 min ago</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Screen 4: Chat Interface */}
                    <div className="absolute inset-0 bg-gradient-to-b from-purple-50 to-pink-50 p-6 opacity-0 pointer-events-none transition-opacity duration-500">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                            <span className="text-white text-lg font-bold">T</span>
                          </div>
                          <div className="text-lg font-bold text-gray-800">Twerce</div>
                        </div>
                        <div className="text-sm text-gray-500">4/4</div>
                      </div>
                      
                      {/* Content */}
                      <div className="space-y-4">
                        <div className="text-lg font-semibold text-gray-800 mb-4">Chat with PrintPro Nigeria</div>
                        <div className="space-y-3">
                          <div className="bg-gray-100 rounded-2xl p-3 ml-8">
                            <div className="text-sm text-gray-600">Can you deliver by Friday?</div>
                          </div>
                          <div className="bg-orange-100 rounded-2xl p-3 mr-8">
                            <div className="text-sm text-orange-700">Yes, absolutely! We can deliver by Friday morning. Would you like to proceed with the order?</div>
                          </div>
                          <div className="bg-gray-100 rounded-2xl p-3 ml-8">
                            <div className="text-sm text-gray-600">Perfect! Let's go ahead.</div>
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mt-4">
                          <div className="text-sm font-semibold text-green-700 text-center">✅ Order Confirmed</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Navigation Dots */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                  </div>

                  {/* Success Overlay */}
                  <div className="absolute inset-0 bg-green-500 bg-opacity-90 flex items-center justify-center opacity-0 pointer-events-none transition-opacity duration-500">
                    <div className="text-center text-white">
                      <div className="text-4xl mb-4">🎉</div>
                      <div className="text-xl font-bold mb-2">Deal Completed!</div>
                      <div className="text-sm opacity-90">Your request has been successfully fulfilled</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg animate-bounce">
                <span className="text-lg">📱</span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-green-500 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg animate-pulse">
                <span className="text-sm">⚡</span>
              </div>
            </div>

            {/* Instructions */}
            <div className="text-center mt-8">
              <p className="text-gray-600 text-sm">
                <span className="font-semibold">💡 Tip:</span> This is a preview of the Twerce mobile experience
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
              How It Works
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display text-gray-900 mb-4 md:mb-6">
              From Frustration to Success in 4 Simple Steps
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-body">
              From request to offers in hours, not weeks.
            </p>
          </div>

          {/* Visual Process Flow */}
          <div className="relative mb-20">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-orange-200 via-orange-300 to-orange-200 transform -translate-y-1/2"></div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  imageUrl: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=80&h=80&fit=crop&crop=center",
                  title: "Post Your RFQ",
                  description: "Create a detailed request for quotation with your specific requirements, budget, and timeline.",
                  color: "bg-blue-100 text-blue-600",
                  step: "1"
                },
                {
                  imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=80&h=80&fit=crop&crop=center",
                  title: "Receive Offers",
                  description: "Get competitive offers from verified suppliers within hours of posting your request.",
                  color: "bg-green-100 text-green-600",
                  step: "2"
                },
                {
                  imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=80&h=80&fit=crop&crop=center",
                  title: "Negotiate & Communicate",
                  description: "Chat directly with suppliers to discuss details, negotiate terms, and clarify requirements.",
                  color: "bg-purple-100 text-purple-600",
                  step: "3"
                },
                {
                  imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=80&h=80&fit=crop&crop=center",
                  title: "Choose & Complete",
                  description: "Select the best supplier and complete your transaction with secure payment processing.",
                  color: "bg-orange-100 text-orange-600",
                  step: "4"
                },
              ].map((step, index) => (
                <div key={index} className="relative group">
                  <div className="relative z-10 bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                    {/* Step Number */}
                    <div className="absolute -top-4 -right-4 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
                      {step.step}
                    </div>
                    
                    {/* Icon */}
                    <div className="w-20 h-20 mb-6 mx-auto">
                      <img 
                        src={step.imageUrl}
                        alt={step.title}
                        className="w-full h-full object-cover rounded-2xl"
                      />
                    </div>
                    
                    {/* Content */}
                    <h4 className="text-xl font-heading text-gray-900 mb-4 text-center">
                      {step.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-center font-body">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Buyer vs Seller Comparison */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Buyer Section */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-3xl mb-4">
                  <UsersIcon className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-3xl font-display text-gray-900 mb-4">
                  For Buyers: Stop Overpaying
                </h3>
                <p className="text-lg text-gray-600 font-body">
                  No more endless supplier calls or settling for high prices. Get competitive quotes from verified suppliers in hours, not weeks.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">💰</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Save 40% on average</h4>
                    <p className="text-gray-600 text-sm">Multiple competitive quotes ensure you never overpay</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">⚡</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Get quotes in hours</h4>
                    <p className="text-gray-600 text-sm">Not days or weeks like traditional procurement</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🛡️</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Verified suppliers only</h4>
                    <p className="text-gray-600 text-sm">Every vendor is thoroughly vetted and rated</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Seller Section */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-3xl mb-4">
                  <CurrencyDollarIcon className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-3xl font-display text-gray-900 mb-4">
                  For Suppliers: Beat Your Competitors
                </h3>
                <p className="text-lg text-gray-600 font-body">
                  Stop losing deals to faster competitors. Get instant alerts for qualified opportunities and close deals 70% faster.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🔔</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Instant opportunity alerts</h4>
                    <p className="text-gray-600 text-sm">Get notified when customers need your services</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📈</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Grow your business</h4>
                    <p className="text-gray-600 text-sm">Access thousands of potential customers</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🤝</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Direct communication</h4>
                    <p className="text-gray-600 text-sm">Chat directly with customers to close deals</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&h=1080&fit=crop&crop=center" 
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
              About Twerce
            </div>
            <h2 className="text-4xl lg:text-5xl font-display text-gray-900 mb-6">
              The B2B Marketplace That Actually Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-body">
              While others promise, we deliver. Twerce connects verified buyers with qualified suppliers, 
              eliminating the pain of traditional procurement while delivering real results: 40% cost savings, 
              70% faster deal closure, and 98% satisfaction rate.
            </p>
          </div>

          {/* Stats Section with Visual Elements */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[
              { number: "5,000+", label: "Active Businesses", imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=64&h=64&fit=crop&crop=center", color: "bg-blue-100" },
              { number: "50,000+", label: "Successful Transactions", imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=64&h=64&fit=crop&crop=center", color: "bg-green-100" },
              { number: "24/7", label: "Customer Support", imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=64&h=64&fit=crop&crop=center", color: "bg-purple-100" },
              { number: "98%", label: "Satisfaction Rate", imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=64&h=64&fit=crop&crop=center", color: "bg-orange-100" },
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className={`${stat.color} rounded-3xl p-6 mb-4 group-hover:scale-105 transition-transform`}>
                  <div className="w-16 h-16 mb-2 mx-auto">
                    <img 
                      src={stat.imageUrl}
                      alt={stat.label}
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Features Grid with Visual Elements */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div>
              <h3 className="text-3xl lg:text-4xl font-display text-gray-900 mb-8">
                Why Businesses Choose Twerce
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed font-body">
                Traditional procurement is broken. Endless supplier calls, unreliable vendors, and missed opportunities 
                cost businesses time and money. Twerce fixes these problems with a platform that actually works.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: "🛡️",
                    title: "Verified Suppliers Only",
                    description: "Every vendor is thoroughly vetted and rated. No more dealing with unreliable suppliers or quality issues.",
                  },
                  {
                    icon: "🕐",
                    title: "Real-time Opportunity Alerts",
                    description: "Get instant notifications when opportunities match your business. Be first to respond, not last to hear.",
                  },
                  {
                    icon: "💰",
                    title: "Guaranteed Cost Savings",
                    description: "Multiple competitive quotes ensure you never overpay. Average 40% savings vs traditional procurement.",
                  },
                  {
                    icon: "🌍",
                    title: "Nigeria-Wide Coverage",
                    description: "Connect with businesses across all states in Nigeria. Local expertise with national reach.",
                  },
                ].map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-200">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-heading text-gray-900 mb-2">
                        {feature.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed font-body">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual Element */}
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-3xl p-8 lg:p-12">
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="text-4xl mb-4">🏢</div>
                    <h4 className="font-semibold text-gray-900 mb-2">Buyers</h4>
                    <p className="text-sm text-gray-600">
                      Post requirements and receive competitive quotes
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="text-4xl mb-4">📊</div>
                    <h4 className="font-semibold text-gray-900 mb-2">Suppliers</h4>
                    <p className="text-sm text-gray-600">
                      Find new opportunities and grow your business
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="text-4xl mb-4">🛡️</div>
                    <h4 className="font-semibold text-gray-900 mb-2">Verified</h4>
                    <p className="text-sm text-gray-600">
                      All businesses are thoroughly vetted
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
                    <div className="text-4xl mb-4">🌍</div>
                    <h4 className="font-semibold text-gray-900 mb-2">Nigeria-Wide</h4>
                    <p className="text-sm text-gray-600">
                      Connect with businesses across Nigeria
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-6 -right-6 bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg animate-bounce">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg animate-pulse">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-3xl p-8 lg:p-12 text-white relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
                <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-white rounded-full -translate-x-8 -translate-y-8"></div>
              </div>
              
              <div className="relative z-10">
                <h3 className="text-3xl lg:text-4xl font-display mb-4">
                  Ready to Transform Your Business?
                </h3>
                <p className="text-xl mb-8 opacity-90 font-body">
                  Join 5,000+ businesses already saving time and money with Twerce across Nigeria. Don't let your competitors get ahead.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => setCurrentStep(1)}
                    className="bg-white text-orange-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors duration-200 font-heading"
                  >
                    Start Saving Today
                  </button>
                  <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-orange-600 transition-all duration-200 font-heading">
                    See Success Stories
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
      </div>
    </div>
  );

  // Step rendering function
  const renderStep = () => {
  switch (currentStep) {
    case 1:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">What do you need?</h2>
          <p className="text-gray-600 mb-8 font-body">Describe your requirement in detail</p>
          
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2 font-body">Describe your requirement *</label>
            <textarea
              placeholder="e.g., I need 200 branded paper bags for my restaurant, or I need office cleaning services for a 3-story building, or I need rice supply for my catering business"
              value={formData.need}
              onChange={(e) => handleInputChange('need', e.target.value)}
              rows={4}
              className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4 font-body">Select category to help us notify the right vendors:</label>
            <div className="grid grid-cols-2 gap-3">
              {loadingCategories ? (
                // Loading skeleton
                Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="p-4 border-2 border-gray-200 rounded-xl animate-pulse"
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-20 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                  </div>
                ))
              ) : (
                categories.map((category, index) => (
                  <button
                    key={index}
                    onClick={() => handleInputChange('category', category.name)}
                    className={`p-4 border-2 rounded-xl transition-all text-left ${
                      formData.category === category.name
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                  >
                    <div className="text-2xl mb-2">{category.icon}</div>
                    <div className="text-sm font-medium mb-1">{category.name}</div>
                    <div className="text-xs text-gray-600 leading-tight">{category.description}</div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      );

    case 2:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">How many do you need?</h2>
          <p className="text-gray-600 mb-8 font-body">Specify the quantity or volume you need</p>
          
          <div className="mb-8">
            <input
              type="text"
              placeholder="e.g., 200 paper bags, 50kg of rice, 10 hours of cleaning"
              value={formData.quantity}
              onChange={(e) => handleInputChange('quantity', e.target.value)}
              className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
            />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-4 font-body">Is this a one-time or recurring need?</p>
            <div className="flex gap-4">
              <button 
                onClick={() => handleInputChange('frequency', 'one-time')}
                className={`flex-1 py-3 px-4 border-2 rounded-xl transition-all ${
                  formData.frequency === 'one-time'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                One-time
              </button>
              <button 
                onClick={() => handleInputChange('frequency', 'recurring')}
                className={`flex-1 py-3 px-4 border-2 rounded-xl transition-all ${
                  formData.frequency === 'recurring'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                Recurring
              </button>
            </div>
          </div>
        </div>
      );

    case 3:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">When do you need it?</h2>
          <p className="text-gray-600 mb-8 font-body">Help vendors understand your timeline</p>
          
          <div className="space-y-4">
            {urgencyOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleInputChange('urgency', option.value)}
                className={`w-full flex items-center p-4 border-2 rounded-xl transition-all text-left ${
                  formData.urgency === option.value
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                <span className="text-2xl mr-3">{option.icon}</span>
                <span className="font-medium">{option.label}</span>
              </button>
            ))}
          </div>

          {formData.urgency === 'custom' && (
            <div className="mt-4">
              <input
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none"
              />
            </div>
          )}
        </div>
      );

    case 4:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">Where should it be delivered?</h2>
          <p className="text-gray-600 mb-8 font-body">Enter your location for delivery or service</p>
          
          <div className="mb-8">
            <div className="relative">
              <LocationMarkerIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="e.g., Surulere, Lagos or Downtown Calgary"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="pickup"
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            />
            <label htmlFor="pickup" className="ml-2 text-gray-700 font-body">
              I'll pick it up myself
            </label>
          </div>
        </div>
      );

    case 5:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">What's your budget? (Optional)</h2>
          <p className="text-gray-600 mb-8 font-body">Help vendors quote realistically - this is optional</p>
          
          <div className="mb-8">
            <div className="relative">
              <CurrencyDollarIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="e.g., ₦50,000 - ₦70,000"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-4 font-body">Or choose a budget range:</p>
            <div className="space-y-3">
              {budgetOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleInputChange('budget', option.range)}
                  className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all text-left ${
                    formData.budget === option.range
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <span className="font-medium font-body">{option.label}</span>
                  <span className="text-gray-500 font-body">{option.range}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );

    case 6:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">Contact Information</h2>
          <p className="text-gray-600 mb-8 font-body">How should vendors reach you?</p>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-body">Full Name *</label>
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Your full name"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-body">WhatsApp Number *</label>
              <div className="relative">
                <PhoneIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  placeholder="+234 80 1234 5678"
                  value={formData.whatsapp}
                  onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-body">Email (Optional)</label>
              <div className="relative">
                <MailIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="allowWhatsapp"
                checked={formData.allowWhatsapp}
                onChange={(e) => handleInputChange('allowWhatsapp', e.target.checked)}
                className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <label htmlFor="allowWhatsapp" className="ml-2 text-gray-700 font-body">
                Vendors can contact me on WhatsApp
              </label>
            </div>
          </div>
        </div>
      );

    case 7:
      return (
        <div className="w-full">
          <h2 className="text-3xl font-display text-gray-900 mb-4">Preview & Confirm</h2>
          <p className="text-gray-600 mb-8 font-body">Review your request before posting</p>
          
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">📝</span>
                <div>
                  <div className="font-medium">{formData.need}</div>
                  {formData.category && <div className="text-sm text-gray-600">Category: {formData.category}</div>}
                  {formData.quantity && <div className="text-sm text-gray-600">{formData.quantity}</div>}
                  {formData.frequency && <div className="text-sm text-gray-600">Frequency: {formData.frequency}</div>}
                </div>
              </div>
              
              {formData.location && (
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📍</span>
                  <span>{formData.location}</span>
                </div>
              )}
              
              {formData.urgency && (
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📅</span>
                  <span>Needed by: {urgencyOptions.find(o => o.value === formData.urgency)?.label}</span>
                </div>
              )}
              
              {formData.budget && (
                <div className="flex items-center">
                  <span className="text-2xl mr-3">💸</span>
                  <span>Budget: {formData.budget}</span>
                </div>
              )}
              
              {formData.whatsapp && (
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📞</span>
                  <span>Contact: {formData.whatsapp}</span>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-orange-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-heading"
          >
            {isSubmitting ? "Posting Request..." : "Post Request"}
          </button>
        </div>
      );

    default:
      return null;
  }
};

// Success state
if (showSuccess) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center px-4">
      <div className="max-w-md mx-auto text-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckIcon className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-display text-gray-900 mb-4">Done!</h2>
          <p className="text-gray-600 mb-8 font-body">
            We're notifying verified vendors now. You'll receive offers shortly.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-orange-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-orange-700 transition-colors font-heading"
            >
              Track my request
            </button>
            <button
              onClick={() => {
                setShowSuccess(false);
                setCurrentStep(0);

                setFormData({
                  need: "",
                  category: "",
                  quantity: "",
                  frequency: "",
                  urgency: "",
                  location: "",
                  budget: "",
                  fullName: "",
                  whatsapp: "",
                  email: "",
                  allowWhatsapp: true
                });
              }}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-colors font-heading"
            >
              Post another request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Show hero section if currentStep is 0, otherwise show form steps
if (currentStep === 0) {
  return (
    <div>
      {renderHeroSection()}
    </div>
  );
}

// Main form view
return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
    {/* Header */}
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <Link to="/">
              <svg
              width="134"
              height="41"
              viewBox="0 0 134 41"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 lg:h-10"
            >
              <rect
                width="21.5018"
                height="21.5019"
                transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.2529 7.83353)"
                fill="#CC5500"
              />
              <rect
                width="21.5018"
                height="21.5019"
                transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.3063 3)"
                fill="#CC5500"
              />
              <rect
                width="14.7925"
                height="14.7925"
                transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.6796 7.67464)"
                fill="white"
              />
              <rect
                width="10.75"
                height="10.5265"
                transform="matrix(0.717251 0.696815 -0.696748 0.717316 15.7197 8.11559)"
                fill="#CC5500"
              />
              <path
                d="M45.8024 31.62V12.69H38.2124V10.62H55.7024V12.69H48.1124V31.62H45.8024ZM58.9051 31.62L54.6751 16.59H56.8651L60.1051 28.59L63.8551 16.59H65.8351L69.5551 28.59L72.8251 16.59H74.9251L70.6651 31.62H68.5351L64.8151 19.59L61.0651 31.62H58.9051ZM83.9959 31.89C82.5559 31.89 81.2459 31.55 80.0659 30.87C78.9059 30.17 77.9759 29.23 77.2759 28.05C76.5959 26.87 76.2559 25.55 76.2559 24.09C76.2559 22.65 76.5859 21.35 77.2459 20.19C77.9059 19.01 78.7959 18.08 79.9159 17.4C81.0359 16.7 82.2859 16.35 83.6659 16.35C85.0059 16.35 86.2059 16.7 87.2659 17.4C88.3459 18.08 89.1959 19.01 89.8159 20.19C90.4559 21.35 90.7759 22.65 90.7759 24.09V24.78H78.4459C78.5659 25.76 78.8759 26.64 79.3759 27.42C79.8959 28.2 80.5659 28.82 81.3859 29.28C82.2059 29.72 83.1059 29.94 84.0859 29.94C84.8659 29.94 85.6259 29.82 86.3659 29.58C87.1059 29.34 87.7259 29 88.2259 28.56L89.6059 30.03C88.7659 30.65 87.8859 31.12 86.9659 31.44C86.0659 31.74 85.0759 31.89 83.9959 31.89ZM78.5059 23.01H88.5559C88.4359 22.11 88.1359 21.31 87.6559 20.61C87.1959 19.89 86.6159 19.33 85.9159 18.93C85.2159 18.51 84.4459 18.3 83.6059 18.3C82.7459 18.3 81.9559 18.5 81.2359 18.9C80.5159 19.3 79.9159 19.86 79.4359 20.58C78.9559 21.28 78.6459 22.09 78.5059 23.01ZM93.8795 31.62V16.59H96.0995V18.72C96.5595 17.94 97.1395 17.34 97.8395 16.92C98.5595 16.48 99.3795 16.26 100.299 16.26C100.819 16.26 101.289 16.33 101.709 16.47V18.51C101.449 18.41 101.189 18.34 100.929 18.3C100.669 18.26 100.409 18.24 100.149 18.24C99.2295 18.24 98.4195 18.51 97.7195 19.05C97.0395 19.57 96.4995 20.33 96.0995 21.33V31.62H93.8795ZM110.156 31.89C108.736 31.89 107.446 31.55 106.286 30.87C105.126 30.17 104.206 29.23 103.526 28.05C102.846 26.87 102.506 25.55 102.506 24.09C102.506 22.63 102.846 21.32 103.526 20.16C104.206 18.98 105.126 18.04 106.286 17.34C107.446 16.64 108.736 16.29 110.156 16.29C111.256 16.29 112.316 16.5 113.336 16.92C114.356 17.32 115.256 17.9 116.036 18.66L114.626 20.28C114.026 19.64 113.336 19.16 112.556 18.84C111.796 18.5 111.006 18.33 110.186 18.33C109.166 18.33 108.236 18.59 107.396 19.11C106.576 19.61 105.916 20.3 105.416 21.18C104.936 22.04 104.696 23.01 104.696 24.09C104.696 25.17 104.936 26.15 105.416 27.03C105.916 27.91 106.586 28.61 107.426 29.13C108.266 29.63 109.196 29.88 110.216 29.88C111.036 29.88 111.806 29.72 112.526 29.4C113.266 29.08 113.956 28.61 114.596 27.99L115.976 29.49C115.176 30.25 114.276 30.84 113.276 31.26C112.276 31.68 111.236 31.89 110.156 31.89ZM125.187 31.89C123.747 31.89 122.437 31.55 121.257 30.87C120.097 30.17 119.167 29.23 118.467 28.05C117.787 26.87 117.447 25.55 117.447 24.09C117.447 22.65 117.777 21.35 118.437 20.19C119.097 19.01 119.987 18.08 121.107 17.4C122.227 16.7 123.477 16.35 124.857 16.35C126.197 16.35 127.397 16.7 128.457 17.4C129.537 18.08 130.387 19.01 131.007 20.19C131.647 21.35 131.967 22.65 131.967 24.09V24.78H119.637C119.757 25.76 120.067 26.64 120.567 27.42C121.087 28.2 121.757 28.82 122.577 29.28C123.397 29.72 124.297 29.94 125.277 29.94C126.057 29.94 126.817 29.82 127.557 29.58C128.297 29.34 128.917 29 129.417 28.56L130.797 30.03C129.957 30.65 129.077 31.12 128.157 31.44C127.257 31.74 126.267 31.89 125.187 31.89ZM119.697 23.01H129.747C129.627 22.11 129.327 21.31 128.847 20.61C128.387 19.89 127.807 19.33 127.107 18.93C126.407 18.51 125.637 18.3 124.797 18.3C123.937 18.3 123.147 18.5 122.427 18.9C121.707 19.3 121.107 19.86 120.627 20.58C120.147 21.28 119.837 22.09 119.697 23.01Z"
                fill="#CC5500"
              />
            </svg>
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/login')}
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    </header>

    {/* Main Content */}
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-gray-700">Step {currentStep} of 7</span>
          <span className="text-sm text-gray-500">{Math.round((currentStep / 7) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-orange-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 7) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Step Content */}
      <div className="flex justify-center">
        <div className="w-full max-w-4xl">
          {renderStep()}
        </div>
      </div>

      {/* Navigation */}
      {currentStep < 7 && (
        <div className="max-w-4xl mx-auto mt-12 flex justify-between">
          <button
            onClick={prevStep}
            className="flex items-center px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Previous
          </button>
          
          <button
            onClick={nextStep}
            disabled={!formData.need || !formData.category}
            className="flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ArrowRightIcon className="w-4 h-4 ml-2" />
          </button>
        </div>
      )}
    </main>
    
    {/* Footer */}
    <Footer />
  </div>
);
};

export default BuyerOnboarding; 